<template>
  <div class="flex min-h-full flex-1 flex-col justify-center pt-24 sm:px-6 lg:px-8">
    <div class="sm:mx-auto sm:w-full sm:max-w-md">
      <WordmarkLogo class="mx-auto" />
    </div>

    <div class="mt-6 sm:mx-auto sm:w-full sm:max-w-[480px]">
      <div class="px-6 pb-6 sm:rounded-lg sm:px-12">
        <p class="mx-auto mb-6 flex justify-center text-xl font-semibold">
          {{ t("msg.setUp2fa") }}
        </p>

        <FormKit
          type="form"
          class="space-y-2"
          :actions="false"
          @submit="handleVerify"
          @submit-invalid="globals.focusFirstError"
        >
          <p v-if="errorMessage" class="text-[#e05454]">{{ t("msg.error") }}: {{ errorMessage }}</p>

          <p class="mb-6">{{ t("msg.scanQr") }}</p>

          <div class="flex justify-center">
            <canvas ref="qrCanvas"></canvas>
          </div>

          <p class="my-6 text-center text-sm">
            {{ t("msg.orCopyKey") }}
            <CopySpan :value="secretKey" />
          </p>

          <FormKit
            type="text"
            :classes="{ outer: { 'max-w-[20em]': false } }"
            :label="t('msg.code')"
            name="code"
            id="code"
            validation="required"
            autocomplete="off"
          />

          <div>
            <button
              type="submit"
              :class="globals.btnClass(theme, highContrast)"
              class="mt-6 flex w-full justify-center rounded-md px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm"
            >
              {{ t("msg.verify") }}
            </button>
          </div>

          <p v-if="fromKnoc" class="cursor-pointer text-center text-white underline" @click="cancel">
            {{ t("common.cancel") }}
          </p>
          <p v-else class="cursor-pointer text-center text-white underline" @click="logout">
            {{ t("msg.backToLogin") }}
          </p>
        </FormKit>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import QRCode from "qrcode";
import { useColorMode } from "@vueuse/core";

import globals from "../globals";
import { useI18n } from "vue-i18n";
import authService from "../services/auth.service.ts";
import WordmarkLogo from "../components/WordmarkLogo.vue";
import { useAuthStore } from "../pinia/auth.ts";
import CopySpan from "../components/CopySpan.vue";

const props = defineProps({
  fromKnoc: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();

const authStore = useAuthStore();
authStore.fetchTheme();
authStore.fetchHighAccessibility();

const router = useRouter();
const route = useRoute();

const theme = computed(() => authStore.theme?.Accent);
const highContrast = computed(() => authStore.theme?.HighAccessibility);
const errorMessage = ref("");

const qrCanvas = ref(null);

const secretKey = ref("");
const secretUrl = ref("");

useColorMode({
  initialValue: "dark",
});

const handleVerify = (fields, node) => {
  const credentials = {
    Code: fields.code,
  };

  authService.twoFactorVerify(credentials).then(
    async () => {
      // Force refresh to get updated TOTP status after setup
      const status = await authStore.fetchStatus({ force: true });
      authStore.fetchTheme();

      if (status.NewUserDashboard) {
        router.push({ path: "/home/new", query: route.query });
      } else {
        router.push({ path: "/home", query: route.query });
      }
    },
    (error) => {
      errorMessage.value = globals.renderError(t, error);
    },
  );
};

const logout = () => {
  try {
    authStore.logout();
  } finally {
    router.push({ name: "Login", query: route.query });
  }
};

const cancel = async () => {
  const status = await authStore.fetchStatus();
  if (status?.NewUserDashboard) {
    router.push({ name: "NewUserDashboard" });
  } else {
    router.push({ name: "Home" });
  }
};

onMounted(() => {
  authService.twoFactorOnboard().then(
    (response) => {
      secretKey.value = response.Secret;
      secretUrl.value = response.URL;

      QRCode.toCanvas(qrCanvas.value, secretUrl.value, function (error) {
        if (error) console.error(error);
      });
    },
    (error) => {
      errorMessage.value = globals.renderError(t, error);
    },
  );
});
</script>
